import { useContext, useEffect, useState } from 'react'
import { com } from '@eidu/form'
import { EntityTypesContext } from '../../io/context/EntityTypes'
import useEntityRepository from '../../io/useEntityRepository'
import DraftFieldValue from '../../domain/entity/DraftFieldValue'
import getNonNullFieldsFromEntity from '../../util/entity/getNonNullFieldsFromEntity'
import EntityRepository from '../../io/EntityRepository'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityId = com.eidu.sharedlib.entity.EntityId
import FieldId = com.eidu.sharedlib.entity.field.FieldId

type UseEntityParams = {
  entityTypeId: EntityTypeId
  entityId: EntityId | undefined
}

export type UseEntityResult = {
  entityType: EntityType | undefined
  entityTypes: ReadonlyMap<EntityTypeId, EntityType> | undefined
  entity: EntityWithLabelAndRelated | undefined
  entityRepository: EntityRepository
  fields: ReadonlyMap<FieldId, DraftFieldValue>
  loading: boolean
  error: string | undefined
}

const useEntity = ({ entityTypeId, entityId }: UseEntityParams): UseEntityResult => {
  const entityTypeRepository = useContext(EntityTypesContext)
  const entityTypes = entityTypeRepository.useAll()
  const entityType = entityTypeRepository.useItem(entityTypeId)
  const typeError = entityTypeRepository.useError()?.let((it) => String(it))
  const typeLoading = entityTypeRepository.useLoading()

  const entityRepository = useEntityRepository(
    {
      typeId: entityType?.id,
      types: entityTypes ?? new Map(),
    },
    [entityType?.id?.toString(), entityTypes?.size]
  )

  // Manage entity loading with own state to avoid calling repository.useItem conditionally
  // (useItem contains hooks internally, so it can't be skipped for the create case)
  const [entity, setEntity] = useState<EntityWithLabelAndRelated | null | undefined>(undefined)
  useEffect(() => {
    if (!entityId || !entityType || !entityTypes) {
      setEntity(undefined)
      return
    }
    setEntity(undefined)
    entityRepository
      .get(entityId)
      .then(setEntity)
      .catch(() => setEntity(null))
  }, [entityId, entityType, entityTypes, entityRepository])

  const entityNotFound = entityId !== undefined && entity === null

  const error = typeError ?? (entityNotFound ? 'Entity not found' : undefined)
  const loading = (typeLoading || (entityId !== undefined && entity === undefined)) && !error

  const [fields, setFields] = useState<ReadonlyMap<FieldId, DraftFieldValue>>(new Map())
  useEffect(() => {
    if (entity) {
      setFields(getNonNullFieldsFromEntity(entity.entity))
    }
  }, [entity])

  return {
    entityType: entityType ?? undefined,
    entityTypes,
    entity: entity ?? undefined,
    entityRepository,
    fields,
    loading,
    error,
  }
}

export default useEntity
